import moment from 'moment';
import 'moment/locale/da';
import { callService } from './api/common';
import { getTrygTealium } from './tracking';

/**
 * Remove properties from an object
 * @param   {object} items            The object which needs props removed
 * @param   {array}  itemsToDelete    Array of the items that needs to be pulled from the object
 * @returns {object}                  Returns the object without the items from the array
 */
export const removeFromObject = (items, itemsToDelete) => {
  const object = { ...items };
  itemsToDelete.forEach(itemToDelete => {
    if (Object.hasOwnProperty.call(object, itemToDelete)) {
      delete object[itemToDelete];
    }
  });
  return object;
};

/**
 * Execute the function if the key is pressed
 * @param   {number}   key        The key that should be pressed for the function to be executed
 * @param   {function} func       The function that should be executed
 * @returns {function}            The exectued function
 */
export const onKeyPress = (key, func) => {
  document.addEventListener('keydown', e => {
    const keycode = ((typeof e.keyCode !== 'undefined' && e.keyCode) ? e.keyCode : e.which);
    // console.log(keycode) - Use this to get the keycodes from the different keys
    if (key === keycode) {
      func();
    }
  });
};

/**
 * Gets a query param from the current URL.
 * @param  {string} name Name of the URL param we want to fetch.
 * @return {Array}      Array of values in the param.
 */
export const getQueryParameterByName = (name) => {
  const urlString = window.location.href;
  const url = new URL(urlString);
  const param = url.searchParams.get(name);
  return param ? param.split('-') : [];
};

/**
 * Gets all the query param from the current URL.
 * @return {Object}      Object of key/values in the param.
 */
export const getAllQueryParameters = () => {
  const query = window.location.search.substring(1);
  const vars = query.split('&');
  return vars.reduce((result, parameter) => {
    const pair = parameter.split('=');
    if (pair.length === 2) {
      const [key, value] = pair;
      return {
        ...result,
        [key]: decodeURIComponent(value),
      };
    }

    return { ...result };
  }, {});
};

/**
 * Traverses through the object returning the variable further down the object
 * @param   {object} object     The object you want to traverse through
 * @param   {string} path       The string of the path you want. ex obj.nthObj.nthObj
 * @returns {*}                 Whatever the object is containing
 */
export const resolvePath = (object, path) => {
  const newValue = path.split(/\./);
  let current = object;
  while (newValue.length) {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    current = current[newValue.shift()];
  }
  return current;
};

/**
 * Makes the function able to fire only once
 * @param   {function} fn       The function that will be fired
 * @returns {}
 */
export const once = (fn, context) => {
  let result;
  return () => {
    if (fn) {
      result = fn.apply(context);
      fn = null; // eslint-disable-line
    }
    return result;
  };
};

/**
 * Check if any of the keys exists in the object
 * @param   {object}    objectToTest    The object which we want to loop over
 * @param   {array}     keysToMatch     Array of the keys we want to match
 * @param   {string}    useChildProp    If keysToMatch array consists of objects and
 *                                      you want match with a child Prop
 * @param   {number}    minLength       If you want to the matched.length to be higher than X.
 * @returns {boolean}
 */
export const anyOfTheKeysExists = (
  objectToTest,
  keysToMatch,
  useChildProp = '',
  minLength = 0
) => {
  if (!keysToMatch) {
    return false;
  }
  return keysToMatch.filter(value => {
    // Fix for IE Edge (with Compatibility View) as Object is sometime a function and not object.
    if (typeof Object !== 'object') {
      return true;
    }
    if (useChildProp !== '') {
      const { [useChildProp]: childProp } = value;

      if (minLength >= 0 && Object.keys(objectToTest).includes(childProp)) {
        return Object.keys(objectToTest[childProp]).length >= minLength;
      }
      return Object.keys(objectToTest).includes(childProp);
    }

    if (minLength >= 0 && Object.keys(objectToTest).includes(value)) {
      return Object.keys(objectToTest[value]).length >= minLength;
    }
    return Object.keys(objectToTest).includes(value);
  }).length > 0;
};

/**
 * Fetches options for a select list.
 * @param  {function} dispatch         Dispatch function from the store.
 * @param  {string} options            Name of initial values field or service to fetch from.
 * @param  {string} [optionsFrom=null] Where to get the options from.
 * @param  {object} [initialValues={}] The initialValues from the JSON.
 * @param  {object} [userData={}]      The userData from the state.
 * @return {function}                  The options to use.
 */
export const getOptions = (
  dispatch,
  options,
  optionsFrom = null,
  initialValues = {},
  userData = {}
) => {
  switch (optionsFrom) {
    case 'userData':
      if (Object.hasOwnProperty.call(userData, options)) {
        return Promise.resolve(userData[options]);
      }
      return Promise.resolve([]);
    case 'initialValues':
      if (Object.hasOwnProperty.call(initialValues, options)) {
        return Promise.resolve(initialValues[options]);
      }
      return Promise.resolve([]);
    case 'service':
      return callService(options, {}, '', userData, dispatch).then(response => {
        return Object.keys(response).reduce((result, key) => {
          if (key === 'usedParams') {
            return result;
          }
          return [
            ...result,
            response[key],
          ];
        }, []);
      });
    default:
      return Promise.resolve([]);
  }
};

/**
 * Recursively merge properties of two objects.
 * @param  {object} objOriginal The original object.
 * @param  {object} objMerge The object to merge.
 * @return {object} The merged object.
 */
export const mergeRecursive = (objOriginal, objMerge) => {
  const result = objOriginal;
  Object.keys(objMerge).reverse().map(p => {
    if (Object.hasOwnProperty.call(objMerge, p)) {
      try {
        // TODO: Check for empty array or object and ignore for this.
        // Property in destination object set; update its value.
        if (objMerge[p].constructor === Object) {
          if (!Object.hasOwnProperty.call(objMerge[p], '_delete')) {
            result[p] = mergeRecursive(result[p], objMerge[p]);
          } else if (objMerge.constructor === Array) {
            result.splice(p, 1);
          } else {
            delete result[p];
          }
        } else if (objMerge[p].constructor === Array) {
          result[p] = mergeRecursive(result[p], objMerge[p]);
        } else if (objMerge[p] === '_delete') {
          delete result[p];
        } else {
          result[p] = objMerge[p];
        }
      } catch (e) {
        // Property in destination object not set; create it and set its value.
        result[p] = objMerge[p];
      }
    }
    return objMerge;
  });

  return result;
};

/**
 * Proper clone object with no side effect.
 * @param object
 * @return {any}
 */
export const clone = object => {
  if (object === undefined) {
    return undefined;
  }
  return JSON.parse(JSON.stringify(object));
};

/**
 * Parser Constants.
 * @type {{CURRENT_YEAR: number, SUB_PRODUCT: string}}
 */
export const PARSER_CONST = {
  CURRENT_YEAR: new Date().getFullYear(),
  SUB_PRODUCT: 'userData[\'subProduct\']',
  CURRENT_DATE: new Date().toLocaleString('da-dk', { dateStyle: 'long' }),
};

/**
 * Replace value with number or currency format.
 * @param {string|number} value
 * @param {boolean} numberFormat
 * @param {boolean} currencyFormat
 * @return {string}
 */
const replaceWithFormat = (value, numberFormat, currencyFormat) => {
  if ((numberFormat || currencyFormat) &&
    !Number.isNaN(Number.parseFloat(value))) {
    let newValue = value;
    if (typeof value === 'string') {
      newValue = newValue.replace(',', '');
    }
    if (currencyFormat) {
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
      }).format(newValue);
    }
    return new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(newValue);
  }
  return value;
};

/**
 * Return if the environment is in prod or not.
 * @param userData
 * @return {boolean}
 */
export const isProd = (userData) => {
  return !userData || !userData.env || userData.env === 'prod';
};

/**
 * Replace value with found token.
 * @param {string} value
 * @param {object} userData
 * @param {string} token
 * @param {string} findInUserData
 * @param {boolean} numberFormat
 * @param {boolean} currencyFormat
 * @param {boolean} firstValueOfArray
 * @return {string}
 */
const replaceWithToken = (
  value,
  userData,
  token,
  findInUserData,
  numberFormat,
  currencyFormat,
  firstValueOfArray
) => {
  if (!PARSER_CONST.TEALIUM_ENV) {
    getTrygTealium().then((utagData) => {
      PARSER_CONST.TEALIUM_ENV = utagData.tealium_environment;
      PARSER_CONST.MOUSEFLOW_ID = utagData.mouseflow_session_id;
    });
  }
  if (findInUserData === 'linkNoHouse') {
    return value.replace(
      token,
      `${location.pathname}?products=${userData[findInUserData]}` // eslint-disable-line
    );
  } else if (Object.hasOwnProperty.call(PARSER_CONST, findInUserData)) {
    return value.replace(
      token,
      replaceWithFormat(PARSER_CONST[findInUserData], numberFormat, currencyFormat)
    );
  } else if (Object.hasOwnProperty.call(userData, findInUserData)) {
    if (!numberFormat && !currencyFormat && value === token) {
      if (firstValueOfArray && Array.isArray(userData[findInUserData])) {
        return userData[findInUserData][0];
      }
      return userData[findInUserData];
    }
    return value.replace(
      token,
      replaceWithFormat(userData[findInUserData], numberFormat, currencyFormat)
    );
  } else if (findInUserData.indexOf('.') !== -1) {
    const keys = findInUserData.split('.');
    let tempData = userData;
    const founds = [];
    keys.forEach(k => {
      if (tempData && Object.hasOwnProperty.call(tempData, k)) {
        tempData = tempData[k];
        founds.push(k);
      }
      if (firstValueOfArray && Array.isArray(tempData)) {
        [tempData] = tempData;
      }
    });
    if (tempData !== undefined && founds.length === keys.length) {
      if (firstValueOfArray && typeof tempData === 'object') {
        console.warn('Can\'t find value for keys', keys, tempData); // eslint-disable-line
      }
      if (typeof tempData !== 'object' && typeof value === 'string') {
        return value.replace(
          token,
          replaceWithFormat(tempData, numberFormat, currencyFormat)
        );
      }
      return tempData;
    }
  }
  return value;
};

/**
 * Return not found value.
 * @param {string} value
 * @param {string} token
 * @param {string} replaceToken
 * @param {string} type
 * @param {object} userData
 * @return {string}
 */
const getNotFoundValue = (value, token, replaceToken, type, userData, ignoreNotFound) => {
  const message = `Can't find "${token}" in ${type}`;
  if (!isProd(userData) && !ignoreNotFound) {
    console.warn(message); // eslint-disable-line
  }
  const replaceValue = isProd(userData) || ignoreNotFound ? '' : message;
  if (isProd(userData) || ignoreNotFound || !value.includes(message)) {
    return value.replace(replaceToken, replaceValue);
  }
  return value;
};

/**
 * Replace dynamic string from user data.
 * Use $[keyString] for string replacement
 * or $(keyMap) which will map to a specific variable in userData which can contain array or object.
 * @param {string}  value
 * @param {object}  userData
 * @param {boolean} numberFormat
 * @param {boolean} currencyFormat
 * @param {boolean} firstValueOfArray
 * @param {boolean} ignoreNotFound
 * @return {string}
 */
export const replaceData = (
  value,
  userData,
  numberFormat = false,
  currencyFormat = false,
  firstValueOfArray = true,
  ignoreNotFound = false
) => {
  let newValue = value;
  // Loop through the userData and replace tokens with its value
  if (userData && typeof newValue === 'string') {
    const findMap = /^\$\((.*?)\)$/;
    // Check match for map replacement. Only works for top level key.
    // Will work for all data types, e.g. string, array and object.
    if (newValue.match(findMap)) {
      const findKey = newValue.match(findMap)[1];
      newValue = userData[findKey];
    } else {
      const find = /\$\[(.*?)\]\]?/gu;
      if (newValue.match(find)) {
        // Allow for two level of token in token like $[topLevel$[subLevel-$[thirdLevel]]].
        newValue.match(find).map(t => {
          const findTwo = /\$\[(.*?)\]\]/;
          const findOne = /\$\[(.*?)\]/;
          let token = t;
          const matchedTwo = t.substr(1).match(findTwo);
          const matchedOne = t.substr(1).match(findOne);
          if (matchedTwo) {
            const nextExpression = matchedTwo[0];
            const matchedLast = nextExpression.substr(1).match(findOne);
            if (matchedLast) {
              token = replaceWithToken(
                token,
                userData,
                matchedLast[0],
                matchedLast[1],
                numberFormat,
                currencyFormat,
                firstValueOfArray
              );
              newValue = newValue.replace(t, token);
            }
            const matchedWhole = token.substr(1).match(findOne);
            token = replaceWithToken(
              token,
              userData,
              matchedWhole[0],
              matchedWhole[1],
              numberFormat,
              currencyFormat,
              firstValueOfArray
            );
            newValue = newValue.replace(t, token);
          } else if (matchedOne) {
            token = replaceWithToken(
              token,
              userData,
              matchedOne[0],
              matchedOne[1],
              numberFormat,
              currencyFormat,
              firstValueOfArray
            );
            newValue = newValue.replace(t, token);
          }
          if (token.match(/\]/)) {
            const findInUserData = token.replace(find, '$1');
            newValue = replaceWithToken(
              newValue,
              userData,
              token,
              findInUserData,
              numberFormat,
              currencyFormat,
              firstValueOfArray
            );
          } else {
            newValue = replaceData(
              newValue,
              userData,
              numberFormat,
              currencyFormat,
              firstValueOfArray
            );
          }
          return true;
        });
      }

      // Remove unused tokens of this type.
      const findUnused = '\\$\\[[^\\]]*\\]';
      if (typeof newValue === 'string' && newValue.match(findUnused)) {
        newValue.match(findUnused).map(token => {
          newValue = getNotFoundValue(newValue, token, token, 'userData', userData, ignoreNotFound);
          return true;
        });
      }
    }
  }
  return newValue;
};

/**
 * Replace dynamic string from translation. Use ${translationKey}
 * @param {object}  translations
 * @param {string}  key
 * @param {object}  userData
 * @return {string}
 */
export const replaceTranslation = (
  translations,
  key,
  userData
) => {
  let newValue = key;
  if (translations && typeof key === 'string') {
    const findSimple = /\$\{(.*?)\}/g;
    const findComplex = /(?:.\$\{(.*?)\})/g;

    if (newValue.match(findComplex)) {
      newValue.match(findComplex).map(token => {
        const findInTranslate = token.replace(findComplex, '$1').replace('\xa0', '-');
        const newTokenComplex = token.substr(0, 1) !== '$' ?
          token.substr(token.indexOf('$')) :
          token;
        if (Object.hasOwnProperty.call(translations, findInTranslate)) {
          newValue = newValue.replace(newTokenComplex, translations[findInTranslate]);
          if (newValue.match(findSimple) || newValue.match(findComplex)) {
            newValue = replaceTranslation(translations, newValue, userData);
          }
        }
        return true;
      });
    } else if (newValue.match(findSimple)) {
      newValue.match(findSimple).map(token => {
        const findInTranslate = token.replace(findSimple, '$1').replace('\xa0', '-');
        const newTokenSimple = token.substr(0, 1) !== '$' ? token.substr(1) : token;
        if (Object.hasOwnProperty.call(translations, findInTranslate)) {
          newValue = newValue.replace(newTokenSimple, translations[findInTranslate]);
        } else {
          newValue = getNotFoundValue(newValue, findInTranslate, newTokenSimple, 'translations',
            userData);
        }
        return true;
      });
    } else if (newValue === '_delete') {
      newValue = '';
    }
  }

  return newValue;
};

/**
 * Return coverage codes and merge coverage codes.
 * @param {array|object} coverages
 * @param {object} coverageMerge
 * @param {object} userData
 * @param {object} packageItem
 * @return array
 */
export const getCoverageCodesAndMergeCodes = (
  coverages,
  coverageMerge,
  userData,
  packageItem = null
) => {
  let coverageCodes = coverages;
  const coverageCodesMerge = {};
  if (coverageCodes && coverageMerge) {
    let codesToFilter = [];
    Object.keys(coverageMerge).forEach(key => {
      if (!coverageMerge[key].showForProduct ||
        userData.basketList.includes(coverageMerge[key].showForProduct)) {
        codesToFilter = codesToFilter.concat(coverageMerge[key].coverages);
        coverageCodesMerge[key] =
          packageItem && packageItem.coverages.reduce((result, coverage) => {
            if (coverage.code === coverageMerge[key].coverages[0]) {
              return coverage.style;
            }
            return result;
          }, '');
      }
    });
    if (Array.isArray(coverageCodes)) {
      coverageCodes = coverageCodes.filter(item => !codesToFilter.includes(item));
    } else {
      Object.keys(coverageCodes).forEach(key => {
        if (codesToFilter.indexOf(key) !== -1) {
          delete coverageCodes[key];
        }
      });
    }
  }

  return [coverageCodes, coverageCodesMerge];
};

/**
 * Fires a CustomEvent of supplied type and detail.
 *
 * @param {string} name
 * @param {object} detail
 */
export const createEvent = (name, detail = null) => {
  window.dispatchEvent(new CustomEvent(name, {
    detail,
  }));
};

/**
 * Fires a 'flow-action' event with the supplied type and data.
 *
 * @param {string} type
 * @param {object} data
 */
export const createFlowEvent = (type, data = null) => {
  createEvent('flow-action', {
    type,
    data,
  });
};

/**
 * Replace all include both replaceTranslation and replaceData and run it multiple times.
 * @param {string} value
 * @param {object} translations
 * @param {object} userData
 * @param {boolean} numberFormat
 * @param {boolean} currencyFormat
 * @param {boolean} firstValueOfArray
 * @param {boolean} ignoreNotFound
 * @return {string}
 */
export const replaceAll = (
  value,
  translations,
  userData,
  numberFormat = false,
  currencyFormat = false,
  firstValueOfArray = true
) => {
  let newValue = value;
  for (let i = 0; i < 4; i++) {
    newValue = replaceData(newValue, userData, numberFormat, currencyFormat, firstValueOfArray);
    newValue = replaceTranslation(translations, newValue, userData);
  }

  return newValue;
};

/**
 * Return the date which calculates policy start date + ovt value offer.
 * @param {string} offerDurationLength
 * @param {string} offerDurationType
 * @param {string} activationDate
 * @return {string}
 */
export const getOfferEndDate = (
  offerDurationLength,
  offerDurationType,
  activationDate
) => {
  let offerEndDate = '';
  if (offerDurationType !== undefined && offerDurationLength !== undefined) {
    if (offerDurationType === 'AAR') {
      offerEndDate = moment(activationDate, 'DD-MM-YYYY').locale('da').add(offerDurationLength, 'years').format('Do MMMM YYYY');
    } else if (offerDurationType === 'MDR') {
      offerEndDate = moment(activationDate, 'DD-MM-YYYY').locale('da').add(offerDurationLength, 'months').format('Do MMMM YYYY');
    }
  }
  return offerEndDate;
};

/**
 * Return the translated name for the products.
 * @param {string} name
 * @return {string}
 */
export const translateName = (name) => {
  let iconNameTranslate;
  if (name === 'Rækkehus') {
    iconNameTranslate = 'terraceHouse';
  } else if (name === 'RækkehusHousehold') {
    iconNameTranslate = 'terraceHouseHousehold';
  } else if (name === 'Hus') {
    iconNameTranslate = 'house';
  } else if (name === 'LejlighedHousehold') {
    iconNameTranslate = 'apartmentHousehold';
  } else if (name === 'HusHousehold') {
    iconNameTranslate = 'houseHousehold';
  } else {
    iconNameTranslate = name;
  }
  return iconNameTranslate.replace(/[A-Z][a-z]*/g, str => `-${str.toLowerCase()}`).replace(/(^-)|(-$)/g, '').replace(/[ ,]/g, '');
};


// WEBPACK FOOTER //
// ./src/utilities/common.js